import { Injectable } from '@angular/core';
import { getStorage, ref, uploadBytes, deleteObject } from 'firebase/storage';

export interface QueuedUpload {
  filename: string;
  property: string; // which photo property this is for (e.g., 'preWorksPhoto', 'postWorksPhoto')
  previousPhotoRef: string | null;
  timestamp: number;
  retryCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PhotoUploadQueueService {
  private readonly UPLOAD_QUEUE_KEY = 'photoUploadQueue';
  private readonly MAX_RETRY_COUNT = 5;
  private readonly DB_NAME = 'photoUploadQueue';
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = 'photos';
  private isProcessingQueue = false;
  private db: IDBDatabase | null = null;

  constructor() {
    console.log('PhotoUploadQueueService initialized');
    
    // Initialize IndexedDB
    this.initDB().then(() => {
      // Process queue on service initialization
      this.processUploadQueue();
    });
    
    // Listen for online event to retry failed uploads
    window.addEventListener('online', () => {
      console.log('Connection restored, processing photo upload queue...');
      this.processUploadQueue();
    });
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);
      
      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB initialized successfully');
        resolve();
      };
      
      request.onupgradeneeded = (event: any) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: 'filename' });
        }
      };
    });
  }

  getUploadQueue(): QueuedUpload[] {
    try {
      const queueJson = localStorage.getItem(this.UPLOAD_QUEUE_KEY);
      return queueJson ? JSON.parse(queueJson) : [];
    } catch (error) {
      console.error('Error reading upload queue:', error);
      return [];
    }
  }

  saveUploadQueue(queue: QueuedUpload[]): void {
    try {
      localStorage.setItem(this.UPLOAD_QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      console.error('Error saving upload queue:', error);
    }
  }

  async addToUploadQueue(upload: QueuedUpload, fileData: string): Promise<void> {
    try {
      // Store photo data in IndexedDB
      await this.savePhotoToIndexedDB(upload.filename, fileData);
      
      // Store metadata in localStorage
      const queue = this.getUploadQueue();
      queue.push(upload);
      this.saveUploadQueue(queue);
      console.log(`Added photo upload to queue: ${upload.filename}`);
    } catch (error) {
      console.error('Failed to add upload to queue:', error);
      throw error;
    }
  }

  async removeFromUploadQueue(filename: string): Promise<void> {
    try {
      // Remove from IndexedDB
      await this.deletePhotoFromIndexedDB(filename);
      
      // Remove from localStorage
      let queue = this.getUploadQueue();
      queue = queue.filter(item => item.filename !== filename);
      this.saveUploadQueue(queue);
      console.log(`Removed photo upload from queue: ${filename}`);
    } catch (error) {
      console.error('Failed to remove upload from queue:', error);
    }
  }

  private async savePhotoToIndexedDB(filename: string, fileData: string): Promise<void> {
    if (!this.db) {
      throw new Error('IndexedDB not initialized');
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.put({ filename, fileData });
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private async getPhotoFromIndexedDB(filename: string): Promise<string | null> {
    if (!this.db) {
      throw new Error('IndexedDB not initialized');
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readonly');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.get(filename);
      
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.fileData : null);
      };
      request.onerror = () => reject(request.error);
    });
  }

  private async deletePhotoFromIndexedDB(filename: string): Promise<void> {
    if (!this.db) {
      return;
    }
    
    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([this.STORE_NAME], 'readwrite');
      const store = transaction.objectStore(this.STORE_NAME);
      const request = store.delete(filename);
      
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  private base64ToBlob(base64: string, contentType: string = 'image/jpeg'): Blob {
    const byteCharacters = atob(base64.split(',')[1] || base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  async processUploadQueue(): Promise<void> {
    if (this.isProcessingQueue) {
      return; // Already processing
    }

    if (!navigator.onLine) {
      console.log('Offline - skipping queue processing');
      return;
    }

    this.isProcessingQueue = true;
    const queue = this.getUploadQueue();
    
    if (queue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }

    console.log(`Processing photo upload queue: ${queue.length} items`);
    
    for (const upload of queue) {
      if (upload.retryCount >= this.MAX_RETRY_COUNT) {
        console.error(`Max retry count reached for ${upload.filename}, removing from queue`);
        await this.removeFromUploadQueue(upload.filename);
        continue;
      }

      try {
        // Fetch photo data from IndexedDB
        const fileData = await this.getPhotoFromIndexedDB(upload.filename);
        
        if (!fileData) {
          console.error(`Photo data missing from IndexedDB for ${upload.filename} — the file cannot be uploaded. Retaining queue entry for manual recovery.`);
          // Do NOT remove from queue: leaving it allows the user to see it's stuck.
          // Increment retry count so it will eventually be abandoned at MAX_RETRY_COUNT.
          const updatedQueue = this.getUploadQueue();
          const itemIndex = updatedQueue.findIndex(item => item.filename === upload.filename);
          if (itemIndex !== -1) {
            updatedQueue[itemIndex].retryCount++;
            this.saveUploadQueue(updatedQueue);
          }
          continue;
        }
        
        const blob = this.base64ToBlob(fileData);
        console.log(`Uploading queued photo to Storage: ${upload.filename} (${(blob.size / 1024).toFixed(1)} KB, attempt ${upload.retryCount + 1}/${this.MAX_RETRY_COUNT})`);
        const storage = getStorage();
        const storageRef = ref(storage, upload.filename);
        
        await uploadBytes(storageRef, blob);
        console.log(`Successfully uploaded queued photo: ${upload.filename}`);
        
        // Delete previous photo if it exists
        if (upload.previousPhotoRef) {
          try {
            const previousRef = ref(storage, upload.previousPhotoRef);
            await deleteObject(previousRef);
            console.log(`Deleted previous photo: ${upload.previousPhotoRef}`);
          } catch (deleteError) {
            console.warn('Could not delete previous photo:', deleteError);
          }
        }
        
        await this.removeFromUploadQueue(upload.filename);
      } catch (error) {
        console.error(`Failed to upload queued photo (attempt ${upload.retryCount + 1}/${this.MAX_RETRY_COUNT}):`, error);
        
        // Update retry count
        const updatedQueue = this.getUploadQueue();
        const itemIndex = updatedQueue.findIndex(item => item.filename === upload.filename);
        if (itemIndex !== -1) {
          updatedQueue[itemIndex].retryCount++;
          this.saveUploadQueue(updatedQueue);
        }
      }
    }
    
    this.isProcessingQueue = false;
  }
}
